import { Flex } from '@frontend/legacy-components';

import { ViewerControlsInfoRow } from 'modules/common/viewer/components';

import { brushHotkeyLabel } from '../session/brushHotkeys';
import { MAPPING_ROLE_DEFINITIONS } from '../session/roles';
import { granularityHotkeyLabel } from '../session/selectionGranularity';
import { redoShortcutLabel, undoShortcutLabel } from '../utils/shortcutLabels';

/**
 * 2D viewer controls (DXF/DWG files) — body only; popover title is shared with 3D.
 *
 * Its own module rather than living in `DxfViewerTools` because both toolbars show
 * it, and the mapping toolbar importing it from the viewing one would be a cycle.
 *
 * The undo/redo rows appear only in mapping mode, because that is the only mode the
 * keys work in — listing them while viewing would promise a shortcut that
 * deliberately does nothing there.
 */
export const DxfViewerControlsInfoRows = ({
  isMobile,
  isMapping = false,
}: {
  isMobile: boolean;
  isMapping?: boolean;
}) => (
  <Flex direction="column" gap={0.5} minW="260px">
    <ViewerControlsInfoRow
      title="Zoom:"
      value={isMobile ? 'Pinch' : 'Scroll'}
    />
    <ViewerControlsInfoRow
      title="Pan:"
      value={isMobile ? 'Drag finger' : 'Drag MMB or Space + drag'}
    />
    <ViewerControlsInfoRow title="Select:" value="Click or drag a box" />
    <ViewerControlsInfoRow title="Add / remove:" value="Shift" />
    <ViewerControlsInfoRow
      title="Select loops / edges:"
      value={`${granularityHotkeyLabel('loop')} / ${granularityHotkeyLabel(
        'edge',
      )}`}
    />
    <ViewerControlsInfoRow title="Other mode while held:" value="Alt" />
    <ViewerControlsInfoRow
      title="One edge from loop mode:"
      value="Double-click"
    />
    <ViewerControlsInfoRow
      title="Box takes what it touches:"
      value="Drag right to left"
    />
    <ViewerControlsInfoRow title="Rotate:" value="Use rotate buttons" />
    <ViewerControlsInfoRow
      title="Reset view:"
      value={isMobile ? 'Double tap' : 'Reset button'}
    />
    {isMapping && !isMobile && (
      <>
        <ViewerControlsInfoRow
          title="Assign role:"
          value="Pick from the popup"
        />
        {MAPPING_ROLE_DEFINITIONS.map(({ role, label }) => (
          <ViewerControlsInfoRow
            key={role}
            title={`Arm ${label.toLowerCase()} brush:`}
            value={brushHotkeyLabel(role)}
          />
        ))}
        <ViewerControlsInfoRow title="Choose (put brush down):" value="Esc" />
        <ViewerControlsInfoRow
          title="Undo mapping:"
          value={undoShortcutLabel()}
        />
        <ViewerControlsInfoRow
          title="Redo mapping:"
          value={redoShortcutLabel()}
        />
      </>
    )}
  </Flex>
);
DxfViewerControlsInfoRows.displayName = 'DxfViewerControlsInfoRows';
